/**
 * I-3 — Frontend Sentry init + PII scrubber.
 *
 * Mirror of `core/sentry_utils.py` on the backend. Drops anything
 * with a PHI-adjacent URL out of the event before it leaves the
 * browser. Imported by `main.tsx` — runs once at module-eval.
 *
 * Opt-in via `VITE_SENTRY_DSN` build-time env. Demo + dev builds
 * without the env stay completely Sentry-free.
 */
import * as Sentry from '@sentry/react'

// PHI-adjacent path prefixes. Includes both backend API endpoints (used by
// fetch / XHR breadcrumbs + event.request.url) and frontend SPA routes (used
// by navigation breadcrumbs and `event.request.url` when an error fires on
// a patient page). A path is PHI if it starts with any of these segments.
const PHI_PATH_FRAGMENTS = [
  // Backend API endpoints
  '/api/patients',
  '/api/medical-records',
  '/api/specialties',
  '/api/prescriptions',
  '/api/rx',
  '/api/lab',
  '/api/imaging',
  '/api/triage',
  '/api/medical-reports',
  '/api/messages',
  '/api/pregnancies',
  '/api/pregnancy-monitoring',
  '/api/deliveries',
  '/api/appointments',
  '/api/invoices',
  // Frontend SPA routes (caught by navigation + ui breadcrumbs)
  '/patients',
  '/medical-records',
  '/prescriptions',
  '/lab',
  '/imaging',
  '/triage',
  '/medical-reports',
  '/messages',
  '/appointments',
  '/billing',
]

function isPhiUrl(url: string | undefined): boolean {
  if (!url) return false
  return PHI_PATH_FRAGMENTS.some((p) => url.includes(p))
}

/**
 * Scrub a URL or path string in-place:
 *  - If the path is PHI-adjacent, mask the trailing segments after the PHI
 *    prefix (so `/patients/90000` → `/patients/<id>`, preserving diagnostic
 *    value of "which surface").
 *  - Always strip the query string on PHI paths (defends against
 *    `?ssn=…` / `?dob=…` / arbitrary PHI in query strings).
 *  - Non-PHI URLs (e.g. `/dashboard`, `/login`) pass through unchanged.
 *  - On parse errors → opaque sentinel rather than risk a leak.
 */
function scrubUrl(url: string | undefined): string | undefined {
  if (!url) return url
  try {
    // Separate path from query. Handle both absolute and relative URLs.
    let pathname = url
    let search = ''
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const u = new URL(url)
      pathname = u.pathname
      search = u.search
    } else {
      const qIdx = url.indexOf('?')
      if (qIdx !== -1) {
        pathname = url.slice(0, qIdx)
        search = url.slice(qIdx)
      }
    }

    const phi = PHI_PATH_FRAGMENTS.find(
      (p) => pathname === p || pathname.startsWith(p + '/'),
    )
    if (!phi) return url  // not PHI-adjacent — keep unchanged

    // Mask anything past the PHI prefix; drop query string entirely.
    const masked = pathname === phi ? phi : `${phi}/<id>`
    return search ? `${masked}?<scrubbed>` : masked
  } catch {
    return '<scrubbed: url-parse-error>'
  }
}

/**
 * beforeBreadcrumb runs as each breadcrumb is added to the buffer (before
 * the event is even built). Single chokepoint for breadcrumb scrubbing —
 * catches navigation `data.from`/`data.to`, fetch/XHR `data.url`, and any
 * other category that happens to carry a URL-shaped string.
 *
 * Failing closed (return null = drop) on any exception so a scrubber bug
 * never produces a leaky breadcrumb.
 */
function scrubBreadcrumb(
  breadcrumb: Sentry.Breadcrumb,
): Sentry.Breadcrumb | null {
  try {
    const data = breadcrumb.data as Record<string, unknown> | undefined
    if (!data) return breadcrumb

    let touched = false

    // navigation: from / to (relative paths)
    if (typeof data.from === 'string') {
      const scrubbed = scrubUrl(data.from)
      if (scrubbed !== data.from) {
        data.from = scrubbed
        touched = true
      }
    }
    if (typeof data.to === 'string') {
      const scrubbed = scrubUrl(data.to)
      if (scrubbed !== data.to) {
        data.to = scrubbed
        touched = true
      }
    }

    // fetch / xhr: url (absolute or relative). The previous behaviour
    // preserved the URL and dropped the body/params; we now ALSO scrub
    // the URL itself so query-string PHI never appears.
    if (typeof data.url === 'string') {
      const scrubbed = scrubUrl(data.url)
      if (scrubbed !== data.url) {
        data.url = scrubbed
        touched = true
      }
    }

    if (touched) data._scrubbed = true
    return breadcrumb
  } catch {
    return null  // drop rather than risk a leak
  }
}

function scrubFrontendEvent(
  event: Sentry.ErrorEvent,
): Sentry.ErrorEvent | null {
  try {
    // 1. Strip user PII — keep only the opaque id.
    // NOTE: `ip_address` is set server-side by Sentry from the request
    // source IP. Clearing it here is belt-and-suspenders only; the real
    // fix is the project-level "Prevent Storing of IP Addresses" toggle
    // (Settings → Security & Privacy on the mashfa-demo project).
    if (event.user) {
      event.user = {
        id: event.user.id,
        ip_address: undefined,
        email: undefined,
        username: undefined,
      }
    }

    // 2. Scrub the request URL + drop body/query-string on PHI URLs.
    const req = (event as { request?: { url?: string; data?: unknown; query_string?: unknown } }).request
    if (req && isPhiUrl(req.url)) {
      req.url = scrubUrl(req.url)
      req.data = '<scrubbed: PHI-adjacent path>'
      req.query_string = '<scrubbed>'
    }

    // 3. Breadcrumbs are scrubbed by `beforeBreadcrumb` (runs as each
    // breadcrumb is added). No event-level loop needed.
    return event
  } catch {
    // Better drop the event than risk a leak.
    return null
  }
}

export function initSentry(): void {
  const env = (import.meta as {
    env?: {
      VITE_SENTRY_DSN?: string
      VITE_SENTRY_ENVIRONMENT?: string
      VITE_SENTRY_RELEASE?: string
      MODE?: string
    }
  }).env ?? {}
  const dsn = env.VITE_SENTRY_DSN
  if (!dsn) return  // dev / demo without DSN → no-op
  Sentry.init({
    dsn,
    environment: env.VITE_SENTRY_ENVIRONMENT ?? env.MODE ?? 'production',
    release: env.VITE_SENTRY_RELEASE,  // git-SHA → suspect-commits, source-map link
    integrations: [Sentry.browserTracingIntegration()],
    // PHI safety: transaction events ship full URLs (e.g. /patients/123)
    // and `beforeSend` runs only on errors, not transactions. Without a
    // `beforeSendTransaction` scrubber we MUST keep this at 0. Breadcrumbs
    // from `browserTracingIntegration` still flow through `beforeBreadcrumb`
    // for error events (the path that actually matters).
    tracesSampleRate: 0,
    sendDefaultPii: false,
    beforeBreadcrumb: scrubBreadcrumb,
    beforeSend: scrubFrontendEvent,
  })
}

// Re-export for ErrorBoundary callers.
export { Sentry }
